import { RiskLevel } from '@/lib/models/user-event';
import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import { uniq } from 'lodash';

const getServiceKey = (event: UserEventModel): string => event.serviceId || event.serviceName;

// Buckets a day's events into contiguous runs of the same service. Switching to a
// different service starts a new bucket, so Claude Code -> ChatGPT -> Claude Code
// yields three buckets rather than two.
export const groupEventsByServiceRun = (dayEvents: UserEventModel[]): UserEventModel[][] => {
  if (!dayEvents.length) {
    return [];
  }
  // Group in ascending startTime order so adjacency checks work naturally, then
  // reverse so the rendered order is newest-first to match the date-section order.
  const sortedEvents = [...dayEvents].sort((a, b) => a.startTime - b.startTime);

  const buckets = sortedEvents.reduce<UserEventModel[][]>((acc, event, index) => {
    if (!index) {
      acc.push([event]);
      return acc;
    }
    const lastEvent = sortedEvents[index - 1];
    if (getServiceKey(event) === getServiceKey(lastEvent)) {
      acc[acc.length - 1].push(event);
    } else {
      acc.push([event]);
    }
    return acc;
  }, []);

  return buckets.reverse();
};

// Within a service bucket, groups events by conversationId, preserving first-seen
// order. Events without a conversationId each become their own single-event group
// so they still render as a row.
export const groupBucketByConversation = (bucket: UserEventModel[]): UserEventModel[][] => {
  const groups: Map<string, UserEventModel[]> = new Map();
  bucket.forEach((event) => {
    const { conversationId, traceId } = event;
    const key = conversationId || `__no-conversation-${traceId}`;
    const existing = groups.get(key);
    if (existing) {
      existing.push(event);
    } else {
      groups.set(key, [event]);
    }
  });
  return [...groups.values()];
};

export const getDomainLabels = (session: UserEventModel[]): string => {
  const allDomainNames = uniq(
    session.flatMap((event) => event.intents.input.topics.map(({ name }) => name)).filter(Boolean)
  );
  return allDomainNames.join(', ');
};

// Priority order for selecting the highest risk level present in a session.
// 'low' is filtered out before this is consulted, so it is not included.
const RISK_LEVEL_PRIORITY: RiskLevel[] = ['high', 'medium', 'unknown'];

type RiskOptions = RiskLevel | null;
export const pickHighestRiskLevel = (levels: RiskOptions[]): RiskOptions => {
  for (const level of RISK_LEVEL_PRIORITY) {
    if (levels.includes(level)) {
      return level;
    }
  }
  return null;
};
